import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Loader2,
  ShieldCheck,
  Star,
  MapPin,
  ExternalLink,
  Share2,
  ArrowLeft,
  Briefcase,
  Award,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 3600) return "recently";
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const days = Math.floor(s / 86400);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function Stars({ value = 0, className = "" }) {
  return (
    <div className={`inline-flex items-center gap-0.5 ${className}`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`w-4 h-4 ${
            n <= Math.round(value)
              ? "fill-[#0E93EC] text-[#0E93EC]"
              : "text-[#C9C6C6]"
          }`}
        />
      ))}
    </div>
  );
}

export default function ProPublicProfile() {
  const { id } = useParams();
  const [pro, setPro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/contractors/${id}/public`);
        if (alive) setPro(data);
      } catch (err) {
        if (alive) setNotFound(err?.response?.status === 404);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    // Fire a private view event (dedup'd server-side per visitor / 30 min).
    // Best-effort: never block or surface an error.
    try {
      const params = new URLSearchParams(window.location.search);
      axios.post(
        `${API}/contractors/${id}/track/view`,
        {
          utm_source: params.get("utm_source") || null,
          utm_medium: params.get("utm_medium") || null,
          utm_campaign: params.get("utm_campaign") || null,
          referrer: (document.referrer || "").slice(0, 512) || null,
        },
        { withCredentials: true }
      ).catch(() => {});
    } catch {
      /* silent */
    }

    return () => {
      alive = false;
    };
  }, [id]);

  const share = async () => {
    // Add utm_source=share so we can attribute shared traffic.
    const base = window.location.href.split("?")[0];
    const url = `${base}?utm_source=share&utm_medium=direct`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: pro?.business_name || "Pro on The Pros Work Hub",
          url,
        });
        return;
      } catch {
        /* fallthrough to clipboard */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D1421] text-[#C9C6C6]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading profile…
      </div>
    );
  }

  if (notFound || !pro) {
    return (
      <div className="min-h-screen bg-[#0D1421] text-[#EEEEEF] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="font-editorial text-4xl md:text-5xl font-light mb-3">
            Profile not found.
          </div>
          <p className="text-[#C9C6C6] mb-6">
            This pro profile is either private or no longer active.
          </p>
          <Link
            to="/"
            className="inline-flex items-center bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-11 px-5 font-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to The Pros Work Hub
          </Link>
        </div>
      </div>
    );
  }

  const verified = pro.verification_status === "verified";

  return (
    <div className="min-h-screen bg-[#0D1421] text-[#EEEEEF]" data-testid="pro-public-profile">
      <SiteHeader />

      {/* Hero */}
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="border-b border-[#C9C6C6]/8"
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 py-10 sm:py-14">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC]">
              — Local pro
            </div>
            <Button
              onClick={share}
              variant="ghost"
              className="rounded-none gap-2 text-[#C9C6C6] hover:text-[#EEEEEF] border border-[#C9C6C6]/15"
              data-testid="pro-profile-share"
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Share</span>
            </Button>
          </div>
          <h1
            className="font-editorial text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[1.02] break-words"
            data-testid="pro-name"
          >
            {pro.business_name}
          </h1>

          {/* Rating + verified + founding */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {pro.reviews_count > 0 && (
              <div className="inline-flex items-center gap-2 bg-[#010204] border border-[#C9C6C6]/10 px-3 py-1.5">
                <Stars value={pro.reviews_avg || 0} />
                <span className="text-sm font-medium" data-testid="pro-rating-avg">
                  {(pro.reviews_avg || 0).toFixed(1)}
                </span>
                <span className="text-xs text-[#C9C6C6]">
                  ({pro.reviews_count} review{pro.reviews_count === 1 ? "" : "s"})
                </span>
              </div>
            )}
            {verified && (
              <div
                className="inline-flex items-center gap-1.5 bg-[#0E93EC] text-[#EEEEEF] px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-semibold"
                data-testid="pro-verified-badge"
              >
                <ShieldCheck className="w-3.5 h-3.5" /> Verified pro
              </div>
            )}
            {pro.is_founding_member && (
              <div
                className="inline-flex items-center gap-1.5 bg-gradient-to-br from-[#C9C6C6] to-[#C9C6C6] text-[#EEEEEF] px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-bold shadow-sm ring-1 ring-[#C9C6C6]/50"
                data-testid="pro-founding-badge"
                title="One of the first 20 pros on The Pros Work Hub"
              >
                <Star className="w-3.5 h-3.5 fill-[#EEEEEF]" /> Founding Member
              </div>
            )}
          </div>

          {/* Facts row */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {pro.trade && (
              <div className="border border-[#C9C6C6]/10 bg-[#010204] p-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1 flex items-center gap-1">
                  <Briefcase className="w-3 h-3" /> Trade
                </div>
                <div className="font-medium text-sm capitalize break-words">{pro.trade}</div>
              </div>
            )}
            {pro.years_in_business != null && (
              <div className="border border-[#C9C6C6]/10 bg-[#010204] p-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1 flex items-center gap-1">
                  <Award className="w-3 h-3" /> Experience
                </div>
                <div className="font-medium text-sm">
                  {pro.years_in_business} year{pro.years_in_business === 1 ? "" : "s"}
                </div>
              </div>
            )}
            {pro.team_size != null && (
              <div className="border border-[#C9C6C6]/10 bg-[#010204] p-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1 flex items-center gap-1">
                  <Users className="w-3 h-3" /> Team
                </div>
                <div className="font-medium text-sm">
                  {pro.team_size} {pro.team_size === 1 ? "person" : "people"}
                </div>
              </div>
            )}
            {(pro.work_zones?.length || 0) > 0 && (
              <div className="border border-[#C9C6C6]/10 bg-[#010204] p-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Service area
                </div>
                <div className="font-medium text-sm break-words">
                  {(pro.work_zones || []).slice(0, 4).join(" · ")}
                  {(pro.work_zones || []).length > 4 && "…"}
                </div>
              </div>
            )}
          </div>

          {/* External links */}
          {(pro.external_profile_links || []).length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2" data-testid="pro-external-links">
              {(pro.external_profile_links || []).map((url, i) => (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] font-medium text-[#C9C6C6] hover:text-[#0E93EC] border border-[#C9C6C6]/10 bg-[#010204] px-3 py-2 hover:border-[#0E93EC] transition-colors break-all"
                >
                  <ExternalLink className="w-3 h-3 shrink-0" />
                  <span className="truncate max-w-[240px]">{url.replace(/^https?:\/\//, "")}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </motion.section>

      {/* Reviews */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 py-12">
        <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-3">
          — Homeowner reviews
        </div>
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <h2 className="font-editorial text-3xl sm:text-4xl lg:text-5xl font-light tracking-tighter">
            {pro.reviews_count === 0
              ? "No reviews yet."
              : `${pro.reviews_count} verified ${pro.reviews_count === 1 ? "review" : "reviews"}.`}
          </h2>
        </div>

        {pro.reviews_count === 0 ? (
          <div className="border border-dashed border-[#C9C6C6]/20 p-10 text-center text-[#C9C6C6] bg-[#010204]">
            This pro is new to The Pros Work Hub. Reviews will show up here once homeowners complete a job with them.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="pro-reviews-list">
            {pro.reviews.map((r) => (
              <div
                key={r.id}
                className="border border-[#C9C6C6]/10 bg-[#010204] p-5"
                data-testid={`pro-review-${r.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <Stars value={r.rating} />
                  <span className="text-[11px] text-[#C9C6C6]">{timeAgo(r.created_at)}</span>
                </div>
                <p className="text-sm text-[#EEEEEF] whitespace-pre-wrap leading-relaxed">
                  {r.body}
                </p>
                <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-[#C9C6C6]">
                  — {r.homeowner_name}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="border-t border-[#C9C6C6]/8 bg-[#010204]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 py-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-2">
              — Need work done?
            </div>
            <h3 className="font-editorial text-3xl sm:text-4xl font-light tracking-tighter">
              Request a quote from up to 3 vetted local pros.
            </h3>
          </div>
          <Link
            to="/#get-quotes"
            className="inline-flex items-center bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-12 px-6 font-medium shrink-0"
          >
            Get free quotes
          </Link>
        </div>
      </section>
    </div>
  );
}
